package br.com.heyagenda.data.repository

import br.com.heyagenda.data.remote.AppointmentDto
import br.com.heyagenda.data.remote.FirebaseAuthDataSource
import br.com.heyagenda.data.remote.FirestoreAppointmentDataSource
import br.com.heyagenda.data.remote.FirestoreClientDataSource
import br.com.heyagenda.domain.model.Appointment
import br.com.heyagenda.domain.model.AppointmentStatus
import br.com.heyagenda.domain.repository.AppointmentRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

class AppointmentRepositoryImpl(
    private val firestoreDataSource: FirestoreAppointmentDataSource,
    private val authDataSource: FirebaseAuthDataSource,
    private val clientDataSource: FirestoreClientDataSource? = null
) : AppointmentRepository {

    private val localAppointments = MutableStateFlow<List<Appointment>>(emptyList())

    override fun getTodayAppointments(): Flow<List<Appointment>> {
        val todayIso = br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()
        return getAppointmentsForDate(todayIso)
    }

    override fun getAppointmentsForDate(dateIso: String): Flow<List<Appointment>> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            println(">>> [AppointmentRepo] User UID is null. Returning local appointments for $dateIso.")
            return localAppointments.map { list ->
                list.filter { it.date == dateIso || (it.date.isBlank() && dateIso == br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()) }
            }
        }

        return firestoreDataSource.observeAppointmentsForDate(uid, dateIso)
            .map { dtos ->
                dtos.map { dto -> dto.toDomain() }
            }
    }

    override fun getAppointmentsInRange(startDateIso: String, endDateIso: String): Flow<List<Appointment>> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            println(">>> [AppointmentRepo] User UID is null. Returning local appointments for $startDateIso..$endDateIso.")
            return localAppointments.map { list ->
                list.filter { it.date in startDateIso..endDateIso }
            }
        }

        return firestoreDataSource.observeAppointmentsInRange(uid, startDateIso, endDateIso)
            .map { dtos ->
                dtos.map { dto -> dto.toDomain() }
            }
    }

    override suspend fun getAppointmentsPaged(limit: Int, startAfterDate: String?): List<Appointment> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            val sorted = localAppointments.value.sortedByDescending { it.date }
            return if (!startAfterDate.isNullOrBlank()) {
                sorted.filter { it.date < startAfterDate }.take(limit)
            } else {
                sorted.take(limit)
            }
        }

        return firestoreDataSource.getAppointmentsPaged(uid, limit, startAfterDate)
            .map { dto -> dto.toDomain() }
    }

    override fun getAppointmentById(appointmentId: String): Flow<Appointment?> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            return localAppointments.map { list -> list.find { it.id == appointmentId } }
        }

        return firestoreDataSource.observeAppointmentById(uid, appointmentId)
            .map { dto -> dto?.toDomain() }
    }


    override suspend fun updateAppointmentStatus(appointmentId: String, status: AppointmentStatus): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val result = firestoreDataSource.updateAppointmentStatus(uid, appointmentId, status.name)
            if (result.isSuccess) {
                return result
            }
        }

        // Fallback in-memory update
        localAppointments.update { list ->
            list.map { item ->
                if (item.id == appointmentId) {
                    item.copy(status = status)
                } else {
                    item
                }
            }
        }
        return Result.success(Unit)
    }

    override suspend fun updateAppointmentPaymentMethod(appointmentId: String, paymentMethod: String): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val result = firestoreDataSource.updateAppointmentPaymentMethod(uid, appointmentId, paymentMethod)
            if (result.isSuccess) {
                return result
            }
        }

        localAppointments.update { list ->
            list.map { item ->
                if (item.id == appointmentId) {
                    item.copy(paymentMethod = paymentMethod)
                } else {
                    item
                }
            }
        }
        return Result.success(Unit)
    }

    override suspend fun completeAppointment(appointmentId: String, paymentMethod: String): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val result = firestoreDataSource.completeAppointment(uid, appointmentId, paymentMethod)
            if (result.isSuccess) {
                return result
            }
        }

        localAppointments.update { list ->
            list.map { item ->
                if (item.id == appointmentId) {
                    item.copy(status = AppointmentStatus.COMPLETED, paymentMethod = paymentMethod)
                } else {
                    item
                }
            }
        }
        return Result.success(Unit)
    }

    override suspend fun addAppointment(appointment: Appointment): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        val dto = appointment.toDto()

        if (uid != null) {
            val result = firestoreDataSource.saveAppointment(uid, dto)
            if (result.isSuccess) {
                if (clientDataSource != null && appointment.clientName.isNotBlank()) {
                    try {
                        clientDataSource.saveClient(
                            uid,
                            br.com.heyagenda.domain.model.SavedClient(
                                name = appointment.clientName.trim(),
                                phone = appointment.clientPhone?.takeIf { it.isNotBlank() },
                                totalAppointments = 1,
                                lastAppointmentDate = appointment.date.takeIf { it.isNotBlank() },
                                lastServiceName = appointment.serviceName.takeIf { it.isNotBlank() }
                            )
                        )
                    } catch (_: Throwable) {}
                }
                return result
            }
        }

        localAppointments.update { list ->
            list + appointment.copy(id = appointment.id.ifBlank { "local_${list.size + 1}" })
        }
        return Result.success(Unit)
    }

    private fun AppointmentDto.toDomain(): Appointment {
        val domainStatus = try {
            AppointmentStatus.valueOf(status)
        } catch (_: Throwable) {
            AppointmentStatus.CONFIRMED
        }

        val currentDt = br.com.heyagenda.domain.util.getPlatformCurrentDateTime()
        val (localDate, localStartTime) = if (startUtcIso.isNotBlank()) {
            br.com.heyagenda.domain.util.DateTimeZoneUtils.utcIsoToLocal(startUtcIso, currentDt.utcOffsetMinutes)
        } else {
            Pair(date, startTime)
        }

        val (_, localEndTime) = if (endUtcIso.isNotBlank()) {
            br.com.heyagenda.domain.util.DateTimeZoneUtils.utcIsoToLocal(endUtcIso, currentDt.utcOffsetMinutes)
        } else {
            Pair(date, endTime)
        }

        return Appointment(
            id = id,
            clientName = clientName,
            clientPhone = clientPhone,
            serviceName = serviceName,
            date = localDate,
            startTime = localStartTime,
            endTime = localEndTime,
            priceFormatted = priceFormatted,
            status = domainStatus,
            paymentMethod = paymentMethod,
            notes = notes,
            startUtcIso = startUtcIso,
            endUtcIso = endUtcIso
        )
    }

    private fun Appointment.toDto(): AppointmentDto {
        val currentDt = br.com.heyagenda.domain.util.getPlatformCurrentDateTime()
        val computedStartUtc = startUtcIso.ifBlank {
            if (date.isNotBlank() && startTime.isNotBlank()) {
                br.com.heyagenda.domain.util.DateTimeZoneUtils.localToUtcIso(date, startTime, currentDt.utcOffsetMinutes)
            } else ""
        }
        val computedEndUtc = endUtcIso.ifBlank {
            if (date.isNotBlank() && endTime.isNotBlank()) {
                br.com.heyagenda.domain.util.DateTimeZoneUtils.localToUtcIso(date, endTime, currentDt.utcOffsetMinutes)
            } else ""
        }

        return AppointmentDto(
            id = id,
            clientName = clientName,
            clientPhone = clientPhone,
            serviceName = serviceName,
            date = date,
            startTime = startTime,
            endTime = endTime,
            priceFormatted = priceFormatted,
            status = status.name,
            paymentMethod = paymentMethod,
            notes = notes,
            startUtcIso = computedStartUtc,
            endUtcIso = computedEndUtc
        )
    }
}
